import React, { useEffect, useRef, useState } from "react";
import { FaMicrophone, FaPause } from "react-icons/fa";
import { toast } from "react-toastify";
import { MAX_VOICE_DURATION_SECONDS } from "../helpers/voiceMessage.helper";
import {
  createRecordedVoiceFile,
  getPreferredVoiceRecorderConfig,
} from "../helpers/voiceRecording.helper";
import {
  getNativeRecorderErrorMessage,
  isNativeVoiceRecorderPlatform,
  startNativeVoiceRecording,
  stopNativeVoiceRecording,
} from "../helpers/voiceRecorder.helper";

type VoiceMessageFieldProps = {
  initialUrl?: string;
  onFileChange: (file: File | null) => void;
  onRemoveExistingChange?: (removed: boolean) => void;
  onRecordingChange?: (recording: boolean) => void;
  title?: string;
  description?: string;
  handleSaveVoiceMessage?: () => void;
  isVoiceSaving?: boolean;
  showSaveButton?: boolean;
};

const VoiceMessageField: React.FC<VoiceMessageFieldProps> = ({
  initialUrl = "",
  onFileChange,
  onRemoveExistingChange,
  onRecordingChange,
  title,
  description,
  handleSaveVoiceMessage,
  isVoiceSaving = false,
  showSaveButton = true,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [voicePreviewUrl, setVoicePreviewUrl] = useState(initialUrl);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordingTimerRef = useRef<number | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);

  useEffect(() => {
    setVoicePreviewUrl(initialUrl);
  }, [initialUrl]);

  const stopMediaTracks = () => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  };

  const clearRecordingTimer = () => {
    if (recordingTimerRef.current !== null) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const revokeIfLocal = (url: string) => {
    if (url && !url.startsWith("http")) {
      URL.revokeObjectURL(url);
    }
  };

  const updatePreview = (file: File | null, nextInitialRemoved = false) => {
    revokeIfLocal(voicePreviewUrl);
    setSelectedFile(file);
    onFileChange(file);
    onRemoveExistingChange?.(nextInitialRemoved);

    if (!file) {
      setVoicePreviewUrl("");
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setVoicePreviewUrl(previewUrl);
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return `${minutes}:${remainder.toString().padStart(2, "0")}`;
  };

  const isNativeRecorder = isNativeVoiceRecorderPlatform();

  const getRecordingErrorMessage = (error: unknown) => {
    const isAndroidDevice = /Android/i.test(navigator.userAgent);
    const domError =
      typeof DOMException !== "undefined" && error instanceof DOMException
        ? error
        : null;

    const errorName = domError?.name || (error as { name?: string })?.name;

    if (!window.isSecureContext) {
      return "Microphone recording only works on HTTPS or localhost.";
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      return "This browser does not support microphone recording.";
    }

    if (
      errorName === "NotAllowedError" ||
      errorName === "PermissionDeniedError"
    ) {
      return isNativeRecorder
        ? "Microphone permission is required to record voice."
        : "Microphone permission is blocked. Please allow mic access in your browser settings.";
    }

    if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
      return isAndroidDevice
        ? "No microphone was found. In Android Emulator, enable host audio input for the virtual microphone and try again."
        : "No microphone was found on this device.";
    }

    if (errorName === "NotReadableError" || errorName === "TrackStartError") {
      return "Your microphone is busy or unavailable. Close other apps using the mic and try again.";
    }

    if (errorName === "SecurityError") {
      return "Microphone access is blocked by browser security settings.";
    }

    if (errorName === "TypeError") {
      return "This browser could not start audio recording.";
    }

    return "Unable to start voice recording on this device.";
  };

  const handleStopRecording = async () => {
    if (isNativeRecorder) {
      try {
        const recordedFile = await stopNativeVoiceRecording();
        updatePreview(recordedFile);
      } catch (error) {
        console.error("Native voice recording stop failed:", error);
        toast.error(getNativeRecorderErrorMessage(error));
      } finally {
        clearRecordingTimer();
        setIsRecording(false);
        onRecordingChange?.(false);
      }
      return;
    }

    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    clearRecordingTimer();
    setIsRecording(false);
    onRecordingChange?.(false);
  };

  const handleStartRecording = async () => {
    if (isNativeRecorder) {
      try {
        await startNativeVoiceRecording();
        recordingChunksRef.current = [];
        setRecordingSeconds(0);
        setIsRecording(true);
        onRecordingChange?.(true);

        recordingTimerRef.current = window.setInterval(() => {
          setRecordingSeconds((previous) => {
            const next = previous + 1;
            if (next >= MAX_VOICE_DURATION_SECONDS) {
              void handleStopRecording();
              toast.info("Voice recording stopped at 3 minutes.");
            }
            return next;
          });
        }, 1000);
      } catch (error) {
        console.error("Native voice recording failed:", error);
        clearRecordingTimer();
        setIsRecording(false);
        onRecordingChange?.(false);
        toast.error(getNativeRecorderErrorMessage(error));
      }
      return;
    }

    if (!window.isSecureContext) {
      toast.error("Microphone recording only works on HTTPS or localhost.");
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("This browser does not support microphone recording.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredRecorderConfig = getPreferredVoiceRecorderConfig();
      let recorder: MediaRecorder;

      try {
        recorder = preferredRecorderConfig?.mimeType
          ? new MediaRecorder(stream, {
              mimeType: preferredRecorderConfig.mimeType,
            })
          : new MediaRecorder(stream);
      } catch (recorderError) {
        recorder = new MediaRecorder(stream);
      }

      recordingChunksRef.current = [];
      mediaRecorderRef.current = recorder;
      mediaStreamRef.current = stream;
      setRecordingSeconds(0);
      setIsRecording(true);
      onRecordingChange?.(true);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        clearRecordingTimer();
        stopMediaTracks();
        setIsRecording(false);
        onRecordingChange?.(false);

        if (!recordingChunksRef.current.length) return;

        const recordedFile = createRecordedVoiceFile(
          recordingChunksRef.current,
          recorder.mimeType ||
            preferredRecorderConfig?.mimeType ||
            "audio/webm",
        );
        updatePreview(recordedFile);
      };

      recorder.start();
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingSeconds((previous) => {
          const next = previous + 1;
          if (next >= MAX_VOICE_DURATION_SECONDS) {
            handleStopRecording();
            toast.info("Voice recording stopped at 3 minutes.");
          }
          return next;
        });
      }, 1000);
    } catch (error) {
      console.error("Voice recording failed:", error);
      stopMediaTracks();
      mediaRecorderRef.current = null;
      setIsRecording(false);
      onRecordingChange?.(false);
      toast.error(getRecordingErrorMessage(error));
    }
  };

  const handleVoiceFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("audio/")) {
      toast.error("Please upload a valid audio file.");
      return;
    }
    updatePreview(file);
  };

  const handleRemove = () => {
    updatePreview(null, Boolean(initialUrl || voicePreviewUrl));
    setSelectedFile(null);
  };

  useEffect(() => {
    return () => {
      clearRecordingTimer();
      stopMediaTracks();
      onRecordingChange?.(false);
      revokeIfLocal(voicePreviewUrl);
    };
  }, [onRecordingChange, voicePreviewUrl]);

  return (
    <div className="rounded-2xl space-y-4">
      <div>
        {title && (
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        )}
        {description && <p className="text-sm text-gray-600">{description}</p>}
      </div>

      <div className="w-full flex flex-col sm:flex-row gap-3">
        {!voicePreviewUrl &&
          (!isRecording ? (
            <button
              type="button"
              onClick={handleStartRecording}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-pink-600 px-4 py-3 text-white font-semibold hover:bg-pink-700 transition-colors"
            >
              <FaMicrophone />
              Start Recording
            </button>
          ) : (
            <button
              type="button"
              onClick={handleStopRecording}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-3 text-white font-semibold hover:bg-red-700 transition-colors"
            >
              <FaPause />
              Stop Recording ({formatDuration(recordingSeconds)})
            </button>
          ))}

        

        <div className="text-sm text-gray-600">
          {isRecording
            ? `Recording in progress: ${formatDuration(recordingSeconds)} / 3:00`
            : selectedFile
              ? `Selected file: ${selectedFile.name}`
              : voicePreviewUrl
                ? "Voice message attached."
                : ""}
        </div>

        {voicePreviewUrl && (
          <audio
            controls
            src={voicePreviewUrl}
            className="w-full"
            onLoadedMetadata={(event) => {
              const duration = Math.round(event.currentTarget.duration || 0);
              if (
                Number.isFinite(duration) &&
                duration > MAX_VOICE_DURATION_SECONDS
              ) {
                toast.error("Voice message must be 3 minutes or less.");
                handleRemove();
              }
            }}
          />
        )}

        {(selectedFile || voicePreviewUrl) && handleSaveVoiceMessage && (
          <>
            <div className="flex flex-row gap-2 align-items-center justify-center">
              {showSaveButton ? (
                <button
                  type="button"
                  onClick={handleSaveVoiceMessage}
                  disabled={isVoiceSaving || isRecording}
                  className="text w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isVoiceSaving
                    ? "Saving..."
                    : isRecording
                      ? "Stop To Save"
                      : "Save"}
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleRemove}
                className="w-full inline-flex gap-2 items-center justify-center rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-600 font-medium hover:bg-red-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VoiceMessageField;
